import { MicrosoftService } from '@api/api/alfred-integrations';
import { Icon } from '@design/components/icon/Icon';
import { Toggle } from '@design/components/toggle/Toggle';
import * as Radix from '@radix-ui/react-dropdown-menu';
import MicrosoftOneNoteIcon from '@static/images/icons/microsoft_one_note.svg';
import MicrosoftOutlookIcon from '@static/images/icons/microsoft_outlook.svg';
import MicrosoftSharePointIcon from '@static/images/icons/microsoft_sharepoint.svg';
import MicrosoftTeamsIcon from '@static/images/icons/microsoft_teams.svg';
import { Option } from '@terminal/components/filter/Option';
import { useByoMcpConnectors } from '@terminal/react-query-hooks/alfred/useByoMcpConnectors';
import { useConnectorRegistryProviderIds } from '@terminal/react-query-hooks/alfred/useConnectorRegistryProviderIds';
import { useMicrosoftServices } from '@terminal/react-query-hooks/alfred/useMicrosoftServices';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import {
  isValidMicrosoftServiceName,
  useMicrosoftConnect,
} from '@terminal/views/alfred/research/connection-apps-panel/hooks/useMicrosoftConnect';
import { useConnectors } from '@terminal/views/alfred/research/use-connectors/useConnectors';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './ConnectorsList.module.scss';

const ICON_SIZE = 16;
const SIDE_OFFSET = 6;
const COLLISION_PADDING = 305;

interface ConnectorOption {
  key: string;
  type: MicrosoftService | string;
  registryProviderId?: string;
  icon: ReactNode;
  name: string;
  connected: boolean;
}

const microsoftServiceIcons: Record<MicrosoftService, ReactNode> = {
  onedrive: <MicrosoftSharePointIcon width={ICON_SIZE} height={ICON_SIZE} />,
  outlook: <MicrosoftOutlookIcon width={ICON_SIZE} height={ICON_SIZE} />,
  onenote: <MicrosoftOneNoteIcon width={ICON_SIZE} height={ICON_SIZE} />,
  teams: <MicrosoftTeamsIcon width={ICON_SIZE} height={ICON_SIZE} />,
};

export const ConnectorsList = () => {
  const { t } = useTranslation(['chat']);
  const navigate = useNavigate();
  const userId = useAuthStore(state => state.user_id);
  const { data: customServices = [] } = useByoMcpConnectors(userId);
  const {
    data: services = [],
    isLoading,
    refetch,
  } = useMicrosoftServices(userId);
  const { connectMicrosoft } = useMicrosoftConnect({
    services,
    isLoadingServices: isLoading,
    refetchServices: refetch,
  });
  const { trackAlfredConnectorToggled } = usePosthogEvents();
  const {
    disabledProviders,
    setProviderEnabled,
    disabledMicrosoftServices,
    setMicrosoftServiceEnabled,
  } = useConnectors(state => ({
    disabledProviders: state.disabledProviders,
    setProviderEnabled: state.setProviderEnabled,
    disabledMicrosoftServices: state.disabledMicrosoftServices,
    setMicrosoftServiceEnabled: state.setMicrosoftServiceEnabled,
  }));
  const [disabledLocalConnectors, setDisabledLocalConnectors] = useState<
    string[]
  >([]);

  const connectedCustomServices = customServices.filter(
    service => service.connected
  );
  const registryProviderIds = useConnectorRegistryProviderIds(
    connectedCustomServices.flatMap(service =>
      service.providerId ? [service.providerId] : []
    )
  );

  const connectors: ConnectorOption[] = [
    ...connectedCustomServices.map(service => ({
      key: `${service.providerId}-${service.serviceId}`,
      type: service.type,
      registryProviderId: service.providerId
        ? registryProviderIds[service.providerId]
        : undefined,
      icon: <Icon iconName="Server" size={ICON_SIZE} />,
      name: service.name ?? service.type,
      connected: service.connected,
    })),
    ...services.map(({ type, connected }) => ({
      key: type,
      type,
      icon: isValidMicrosoftServiceName(type) ? (
        microsoftServiceIcons[type]
      ) : (
        <Icon iconName="Server" size={ICON_SIZE} />
      ),
      name: isValidMicrosoftServiceName(type)
        ? t(`chat:services.${type}.name`)
        : type,
      connected,
    })),
  ];

  const isConnectorEnabled = ({
    key,
    type,
    registryProviderId,
  }: ConnectorOption) => {
    if (registryProviderId) {
      return !disabledProviders.includes(registryProviderId);
    }
    if (isValidMicrosoftServiceName(type)) {
      return !disabledMicrosoftServices.includes(type);
    }
    return !disabledLocalConnectors.includes(key);
  };

  const handleConnectorToggle = (
    connector: ConnectorOption,
    checked: boolean
  ) => {
    if (connector.registryProviderId) {
      setProviderEnabled(connector.registryProviderId, checked);
    } else if (isValidMicrosoftServiceName(connector.type)) {
      setMicrosoftServiceEnabled(connector.type, checked);
    } else {
      setDisabledLocalConnectors(keys =>
        checked
          ? keys.filter(k => k !== connector.key)
          : [...keys, connector.key]
      );
    }

    trackAlfredConnectorToggled({
      source: POSTHOG_TRACKING_SOURCES.ALFRED,
      type: connector.type,
      enabled: checked,
    });
  };

  const handleAddConnector = () => {
    navigate({
      pathname: appPaths.alfredSettings,
      search: `?${queryKeys.activeTab}=integrations`,
    });
  };

  return (
    <Radix.Sub>
      <Radix.SubTrigger
        className={styles.subTrigger}
        data-testid="upload-dropdown-connectors"
      >
        <div className={styles.optionWrapper}>
          <Icon iconName="DashboardNew" size={ICON_SIZE} />
          <span className={styles.label}>
            {t('chat:userUpload.connectors')}
          </span>
        </div>
        <Icon
          iconName="ChevronLightRight"
          size={12}
          fillColor="var(--icon-default)"
        />
      </Radix.SubTrigger>
      <Radix.Portal>
        <Radix.SubContent
          className={styles.subMenuContent}
          sideOffset={SIDE_OFFSET}
          collisionPadding={COLLISION_PADDING}
          data-testid="upload-dropdown-connectors-content"
        >
          <Option
            option={{
              key: 'addConnector',
              name: t('chat:userUpload.addConnector'),
              value: {},
            }}
            handleClick={handleAddConnector}
            renderCustomFilterOptionLabel={() => {
              return (
                <div className={styles.optionWrapper}>
                  <Icon iconName="Add" size={ICON_SIZE} />
                  <span className={styles.label}>
                    {t('chat:userUpload.addConnector')}
                  </span>
                </div>
              );
            }}
          />
          <div className={styles.separator} />
          {connectors.map(connector => {
            const label = (
              <span className={styles.optionWrapper}>
                {connector.icon}
                <span className={styles.label}>{connector.name}</span>
              </span>
            );

            return (
              <Radix.Item
                key={connector.key}
                className={styles.connectorItem}
                onSelect={event => event.preventDefault()}
              >
                {connector.connected ? (
                  <Toggle
                    className={styles.connectorToggle}
                    id={connector.key}
                    isChecked={isConnectorEnabled(connector)}
                    onToggle={checked =>
                      handleConnectorToggle(connector, checked)
                    }
                    leftLabel={label}
                    data-testid={`upload-dropdown-connector-${connector.key}`}
                  />
                ) : (
                  <button
                    type="button"
                    className={styles.disconnectedConnector}
                    data-testid={`upload-dropdown-connector-${connector.key}-disconnected`}
                  >
                    {label}
                    <Icon
                      iconName="ExclamationFill"
                      size={ICON_SIZE}
                      fillColor="var(--icon-warning)"
                      onClick={() => {
                        if (isValidMicrosoftServiceName(connector.key)) {
                          connectMicrosoft(connector.key);
                        }
                      }}
                    />
                  </button>
                )}
              </Radix.Item>
            );
          })}
        </Radix.SubContent>
      </Radix.Portal>
    </Radix.Sub>
  );
};
